import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import SEO from "@/components/SEO";

const Privacy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for KEISH Design Studio. Learn how we collect, use, and protect your personal information."
        noindex
      />
      <div className="min-h-screen bg-background text-foreground">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
          {/* Back link */}
           <Link
             to="/"
             className="inline-flex items-center gap-2 font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors mb-12"
           >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Home
          </Link>

          <h1 className="font-display italic text-4xl md:text-5xl tracking-[-0.02em] mb-4">
            Privacy Policy
          </h1>
           <p className="font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground mb-12">
             Last Updated: February 13, 2026
           </p>

           <div className="space-y-10 font-body text-ts-xs md:text-ts-sm leading-relaxed text-foreground/80">
            {/* Intro */}
            <section>
              <p>
                KEISH Design Studio ("we," "us," or "our") operates the website{" "}
                <strong className="text-foreground">keish.studio</strong> and provides web design services,
                digital templates, and related products. This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you visit our website, purchase our products,
                or engage our services.
              </p>
              <p className="mt-3">
                By using our website, you consent to the practices described in this policy. If you do not
                agree, please discontinue use of our site.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                1. Information We Collect
              </h2>

               <h3 className="font-body text-ts-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground mt-6 mb-3">
                 Personal Information You Provide
               </h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Name and email address (when you contact us or submit a form)</li>
                <li>Payment and billing information (processed securely through third-party payment processors)</li>
                <li>Business name and project details (when you inquire about services)</li>
                <li>Any other information you voluntarily provide via email or contact forms</li>
              </ul>

               <h3 className="font-body text-ts-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground mt-6 mb-3">
                 Information Collected Automatically
               </h3>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>IP address and approximate geographic location</li>
                <li>Browser type, device type, and operating system</li>
                <li>Pages visited, time spent, and navigation patterns</li>
                <li>Referring URL and exit pages</li>
                <li>Click and interaction data on portfolio items, CTAs, and links</li>
              </ul>

               <h3 className="font-body text-ts-2xs font-semibold tracking-[0.15em] uppercase text-muted-foreground mt-6 mb-3">
                 Purchase Information
               </h3>
              <p>
                When you purchase digital templates or products through our site or linked sale pages,
                we may collect transaction details, shipping/billing address (if applicable), and order history.
                Payment processing is handled by third-party providers (e.g., Stripe, Gumroad, or Lemon Squeezy)
                — we do not store your full credit card information.
              </p>
            </section>

            {/* Analytics & Tracking */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                2. Analytics & Tracking Technologies
              </h2>
              <p className="mb-4">
                We use analytics tools to understand how visitors interact with our site. This helps us improve
                our content, design, and user experience.
              </p>

              {/* GA4 Table */}
              <div className="border border-border rounded-sm overflow-x-auto mb-6">
                <table className="w-full text-left text-[13px] min-w-[600px]">
                   <thead>
                     <tr className="bg-muted/50">
                       <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                         Technology
                       </th>
                       <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                         Provider
                       </th>
                       <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                         Purpose
                       </th>
                       <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                         Data Collected
                       </th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-border">
                     <tr>
                       <td className="px-4 py-3 font-medium text-foreground">Google Analytics 4</td>
                       <td className="px-4 py-3">Google LLC</td>
                       <td className="px-4 py-3">Site analytics, traffic measurement</td>
                       <td className="px-4 py-3">Page views, session duration, device info, geographic region, referral source</td>
                     </tr>
                     <tr>
                       <td className="px-4 py-3 font-medium text-foreground">Google Tag Manager</td>
                       <td className="px-4 py-3">Google LLC</td>
                       <td className="px-4 py-3">Tag deployment & event tracking</td>
                       <td className="px-4 py-3">Custom events (CTA clicks, portfolio interactions, social link clicks)</td>
                     </tr>
                   </tbody>
                </table>
              </div>

              <p className="text-ts-xs text-muted-foreground">
                Google Analytics uses cookies and similar technologies. You can opt out by installing the{" "}
                <a
                  href="https://tools.google.com/dlpage/gaoptout"
                  target="_blank"
                  rel="noopener noreferrer"
                   className="link-dotted"
                >
                  Google Analytics Opt-out Browser Add-on
                </a>{" "}
                or adjusting your browser's cookie settings.
              </p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                3. Cookies & Similar Technologies
              </h2>

              <div className="border border-border rounded-sm overflow-x-auto mb-4">
                <table className="w-full text-left text-[13px] min-w-[600px]">
                   <thead>
                     <tr className="bg-muted/50">
                       <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                         Cookie Type
                       </th>
                       <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                         Purpose
                       </th>
                       <th className="px-4 py-3 font-semibold text-foreground text-ts-2xs tracking-[0.1em] uppercase">
                         Duration
                       </th>
                     </tr>
                   </thead>
                  <tbody className="divide-y divide-border">
                    <tr>
                      <td className="px-4 py-3 font-medium text-foreground">Essential</td>
                      <td className="px-4 py-3">Site functionality, session management</td>
                      <td className="px-4 py-3">Session</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-foreground">Analytics (_ga, _ga_*)</td>
                      <td className="px-4 py-3">Distinguish unique users, measure traffic</td>
                      <td className="px-4 py-3">Up to 2 years</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-foreground">Performance</td>
                      <td className="px-4 py-3">Page load metrics, error tracking</td>
                      <td className="px-4 py-3">Session</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-medium text-foreground">Third-Party (Payment)</td>
                      <td className="px-4 py-3">Secure checkout, fraud prevention</td>
                      <td className="px-4 py-3">Varies by provider</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p>
                You can manage cookie preferences through your browser settings. Note that disabling certain
                cookies may affect site functionality.
              </p>
            </section>

            {/* How We Use */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                4. How We Use Your Information
              </h2>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>To respond to inquiries and provide requested services</li>
                <li>To process and fulfill digital product purchases and template orders</li>
                <li>To send transactional emails (order confirmations, project updates)</li>
                <li>To analyze site performance and improve user experience</li>
                <li>To track engagement with portfolio items, CTAs, and social links via custom analytics events</li>
                <li>To detect and prevent fraud or abuse</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            {/* Sharing */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                5. Information Sharing & Disclosure
              </h2>
              <p className="mb-3">
                We do <strong className="text-foreground">not</strong> sell your personal information. We may share data with:
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>
                  <strong className="text-foreground">Service Providers:</strong> Payment processors, email
                  services, hosting providers, and analytics platforms that help us operate our business
                </li>
                <li>
                  <strong className="text-foreground">Legal Requirements:</strong> When required by law,
                  subpoena, or to protect our rights and safety
                </li>
                <li>
                  <strong className="text-foreground">Business Transfers:</strong> In the event of a merger,
                  acquisition, or sale of assets
                </li>
              </ul>
            </section>

            {/* E-Commerce */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                6. E-Commerce & Digital Products
              </h2>
              <p className="mb-3">
                Our website links to sale pages where you can purchase digital templates and products.
                When you make a purchase:
              </p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Payment is processed by third-party providers who maintain their own privacy policies</li>
                <li>We receive order details (name, email, product purchased, transaction ID) to fulfill your order</li>
                <li>Digital products are delivered electronically; we retain purchase records for support and legal purposes</li>
                <li>Refund and return policies are governed by our <Link to="/terms" className="link-dotted">Terms of Service</Link></li>
              </ul>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                7. Data Security
              </h2>
              <p>
                We implement reasonable technical and organizational measures to protect your personal
                information, including HTTPS encryption, secure hosting infrastructure, and access controls.
                However, no method of electronic transmission or storage is 100% secure.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                8. Data Retention
              </h2>
              <p>
                We retain personal information for as long as necessary to fulfill the purposes described
                in this policy, comply with legal obligations, resolve disputes, and enforce agreements.
                Analytics data is retained according to Google Analytics' default retention settings
                (currently 14 months for GA4).
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                9. Your Rights
              </h2>
              <p className="mb-3">Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Access the personal data we hold about you</li>
                <li>Request correction or deletion of your data</li>
                <li>Opt out of analytics tracking</li>
                <li>Withdraw consent for data processing</li>
                <li>Lodge a complaint with a data protection authority</li>
              </ul>
              <p className="mt-3">
                To exercise any of these rights, please contact us at{" "}
                 <a href="mailto:madi@keish.studio" className="link-dotted">
                  madi@keish.studio
                </a>.
              </p>
            </section>

            {/* Children */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                10. Children's Privacy
              </h2>
              <p>
                Our website and services are not directed to individuals under the age of 16. We do not
                knowingly collect personal information from children. If you believe a child has provided
                us with personal data, please contact us immediately.
              </p>
            </section>

            {/* Third-Party Links */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                11. Third-Party Links
              </h2>
              <p>
                Our website may contain links to external sites (social media, sale platforms, client websites).
                We are not responsible for the privacy practices of these sites and encourage you to review
                their privacy policies.
              </p>
            </section>

            {/* Changes */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                12. Changes to This Policy
              </h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page
                with an updated "Last Updated" date. Continued use of our site after changes constitutes
                acceptance of the revised policy.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="font-display italic text-2xl mb-4 text-foreground">
                13. Contact Us
              </h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="mt-3 p-5 border border-border rounded-sm bg-muted/30">
                <p className="font-semibold text-foreground">KEISH Design Studio</p>
                <p className="mt-1">
                  Email:{" "}
                  <a href="mailto:madi@keish.studio" className="link-dotted">
                    madi@keish.studio
                  </a>
                </p>
                <p className="mt-1">
                  Website:{" "}
                  <a href="https://keish.studio" className="link-dotted">
                    keish.studio
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;
